// Periodic + visibility-driven `registration.update()` poller.
//
// Long-lived p2claw tabs stay on stale bundles. The browser only
// byte-checks `sw.js` against the registered SW on navigation OR
// its own ~24 h timer, so a tab that sits open without navigating
// (which is what most p2claw apps look like) won't pick up a new
// bundle until manually reloaded — even though `sw.ts`'s
// `skipWaiting()` + `clients.claim()` plus `wrapper/sw-handoff.ts`'s
// `controllerchange` re-handoff would silently swap the bundle the
// moment the browser noticed.
//
// This poller pokes the browser to NOTICE. Three triggers:
//
//   1. Boot: a one-shot `update()` shortly after the registration
//      hand-off completes. Catches the case where the page was
//      opened to a deployed-but-not-yet-checked bundle.
//   2. `visibilitychange` to visible: an immediate `update()`.
//      Combined with the visibility-reconnect hook this means the
//      first thing that happens when a user returns to a long-lived
//      tab is a fresh-bundle check + a fresh DC.
//   3. Periodic: every 30 minutes WHILE visible. The interval is
//      cleared on hidden (don't burn battery on a tab the user
//      can't see) and re-armed on visible.
//
// The actual swap is silent — no toast, no reload prompt — because
// `sw.ts`'s skip-waiting + claim handles it transparently. If we
// later want a "new version available" UX, that's a follow-up; the
// auto-apply behavior already makes long-lived tabs match
// `vite build` + deploy without user action.

/** Default 30-minute poll interval. Production sweet spot: rare
 *  enough that tabs aren't hammering edge with `sw.js` HEAD checks,
 *  short enough that a new deploy reaches every visible session
 *  inside a half-hour. Overridable for tests. */
export const DEFAULT_UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000;

/** Default boot-time delay before the one-shot update fires. Small
 *  enough that the check is effectively immediate from the user's
 *  perspective; non-zero so we don't compete with the wrapper's
 *  hot path on first paint (registerServiceWorker / installSwHandoff
 *  / mountIframe all sit on the same microtask queue). */
export const DEFAULT_BOOT_UPDATE_DELAY_MS = 1_000;

export interface SwUpdatePollOptions {
  /** The registration returned by `navigator.serviceWorker.register`. */
  registration: ServiceWorkerRegistration;
  /** Defaults to ambient `document`. */
  document?: Document;
  /**
   * Test seam — defaults to `globalThis.setTimeout`. The poll
   * interval uses this too (we don't want a separate `setInterval`
   * mock; chained `setTimeout` is easier to drive in tests).
   */
  setTimeout?: (cb: () => void, ms: number) => unknown;
  /** Test seam — defaults to `globalThis.clearTimeout`. */
  clearTimeout?: (id: unknown) => void;
  /** Override `DEFAULT_UPDATE_POLL_INTERVAL_MS`. */
  intervalMs?: number;
  /** Override `DEFAULT_BOOT_UPDATE_DELAY_MS`. */
  bootDelayMs?: number;
  /**
   * Test seam — invoked instead of `registration.update()`. Defaults
   * to a thin wrapper that calls `registration.update()` and
   * swallows errors (a transient network blip or a 404 on `sw.js`
   * shouldn't surface to the user). Returns a promise the poller
   * awaits; concurrent overlapping calls are de-duped via the
   * in-flight flag.
   */
  runUpdate?: () => Promise<void>;
  /** Optional diagnostic logger. */
  logger?: (message: string) => void;
}

export interface SwUpdatePollHandle {
  /** Stop all timers + listeners. Idempotent. */
  dispose(): void;
}

/**
 * Install the registration-update poller. Returns a handle whose
 * `dispose()` tears it down — the production wrapper never disposes
 * (the SW lives for the tab's lifetime), but tests use it so a
 * single test doesn't leak timers into the next.
 */
export function installSwUpdatePoll(
  opts: SwUpdatePollOptions,
): SwUpdatePollHandle {
  const doc = opts.document ?? document;
  const setTimeoutFn =
    opts.setTimeout ?? ((cb: () => void, ms: number) => setTimeout(cb, ms));
  const clearTimeoutFn =
    opts.clearTimeout ??
    ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
  const intervalMs = opts.intervalMs ?? DEFAULT_UPDATE_POLL_INTERVAL_MS;
  const bootDelayMs = opts.bootDelayMs ?? DEFAULT_BOOT_UPDATE_DELAY_MS;
  const log = opts.logger ?? (() => {});
  const runUpdate =
    opts.runUpdate ??
    (async () => {
      try {
        await opts.registration.update();
      } catch {
        // `update()` can throw on a 404 / network failure; not our
        // problem. The next tick will try again.
      }
    });

  let disposed = false;
  /** Currently-armed periodic timer, if any. Held as a setTimeout id
   *  rather than a setInterval so tests can drive it deterministically
   *  one tick at a time. */
  let periodicTimer: unknown = null;
  /** True while a `runUpdate()` is in flight; suppresses overlapping
   *  re-entries (a visibilitychange that fires while the periodic
   *  tick is mid-fetch shouldn't double the call). */
  let inFlight = false;

  function clearPeriodic(): void {
    if (periodicTimer !== null) {
      clearTimeoutFn(periodicTimer);
      periodicTimer = null;
    }
  }

  function schedulePeriodic(): void {
    if (disposed) return;
    if (doc.visibilityState !== "visible") return;
    clearPeriodic();
    periodicTimer = setTimeoutFn(() => {
      periodicTimer = null;
      void tick();
    }, intervalMs);
  }

  async function tick(): Promise<void> {
    if (disposed) return;
    if (inFlight) {
      schedulePeriodic();
      return;
    }
    inFlight = true;
    log("p2claw:sw-update:tick");
    try {
      await runUpdate();
    } finally {
      inFlight = false;
    }
    schedulePeriodic();
  }

  const onVisibilityChange = (): void => {
    if (disposed) return;
    if (doc.visibilityState === "visible") {
      log("p2claw:sw-update:visible");
      // Fire an immediate update + re-arm the periodic timer.
      void tick();
    } else {
      log("p2claw:sw-update:hidden");
      // Clear the periodic timer; we re-arm on visible.
      clearPeriodic();
    }
  };

  // Boot one-shot: fire shortly after install. We don't run it
  // synchronously because the wrapper has just finished a series of
  // hot-path awaits (`registerServiceWorker`, `installSwHandoff`,
  // `mountIframe`); a small delay lets the page paint first.
  const bootTimer = setTimeoutFn(() => {
    void tick();
  }, bootDelayMs);

  doc.addEventListener("visibilitychange", onVisibilityChange);

  // If the page started visible (the common case — apps almost
  // always boot in foreground), arm the periodic timer alongside
  // the boot one-shot. If the page started hidden (rare: a
  // background tab that received a wake), we wait for the first
  // visible transition.
  if (doc.visibilityState === "visible") {
    schedulePeriodic();
  }

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      doc.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeoutFn(bootTimer);
      clearPeriodic();
    },
  };
}
